import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as DeviceStatusIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as ChargerIcon } from '../../assets/icons/sidebar/sidebar-device.svg';
// import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  PersonalDeviceListingProperties,
  PersonalDeviceListingRequest,
} from '../../interface/personal-device.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import { refreshDevices } from '../../redux/slices/Partners/DeviceManagementSlice';
import {
  disableFiltersApplied,
  fetchAllDevices,
  fetchListChargingPointFilters,
  // setSelectedDevice,
  updateChargingPointFilters,
  setSortData,
} from '../../redux/slices/PersonalDeviceSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../shared-components/table-side-panel/hooks/useTableSidePanel';
import PersonalDeviceSidePanel from '../../shared-components/table-side-panel/PersonalDeviceSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import personalDeviceApi from '../../utils/api/v2/personal-chargers';
import DeviceViewModel from '../charger-pages/advanced-charger-management/components/DeviceViewModel';

interface DeviceModalViewProperties {
  openModal: boolean;
  chargingPointSn: string;
}

interface StatusCountsInterface {
  totalDeviceCount: number;
  totalOnlineDeviceCount: number;
  totalOfflineDeviceCount: number;
  ac: number;
  dc: number;
  hybrid: number;
  CCS1: number;
  CCS2: number;
  Type2: number;
  CHAdeMO: number;
  NACS: number;
  J1772: number;
}

/**
 * Component to display list of devices
 * @returns {any} The result
 */
function PersonalDeviceTable() {
  const [deviceModalView, setDeviceModalView] = useState<DeviceModalViewProperties>({
    openModal: false,
    chargingPointSn: '',
  });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const {
    devices,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.personalDevice);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const statusCounts: StatusCountsInterface = {
    totalDeviceCount: 0,
    totalOnlineDeviceCount: 0,
    totalOfflineDeviceCount: 0,
    ac: 0,
    dc: 0,
    hybrid: 0,
    CCS1: 0,
    CCS2: 0,
    Type2: 0,
    CHAdeMO: 0,
    NACS: 0,
    J1772: 0,
  };

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('persnalChargers'), icon: <ChargerIcon />, count: totalResults }),
    );
  }, [totalResults]);

  /**
   * UI of Status
   * @param {any} device - The device
   * @returns {any} The result
   */
  const renderStatus = (device: PersonalDeviceListingProperties) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );
  const navigate = useNavigate();
  const location = useLocation();

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    sessions: devices,
    disableEdit: true,
    disableDelete: false,
    /**
     * Default handler for open device details
     * @param {any} session - The session
     * @param {any} newTab - The newTab
     */
    onOpenDeviceDetails: (session: any, newTab: boolean) => {
      const id = session?.id || session?._id;
      const name = session?.chargingPointName || '';
      const sourcePath = location.pathname + location.search;
      if (newTab) {
        window.open(
          `/ev-admin/charger-expanded/${id}/device-details?searchBy=${name}&source=${sourcePath}`,
          '_blank',
        );
      } else {
        navigate(
          {
            pathname: `/ev-admin/charger-management/${id}/device-details`,
            search: `?searchBy=${name}&source=${sourcePath}&type=personal-charger`,
          },
          { state: { source: sourcePath } },
        );
      }
    },
  });

  const column = [
    {
      label: t('endConsumerName'),
      accessorKey: 'userCustomId' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
      /**
       * Custom render: show owner full name with userCustomId as sub-text
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: PersonalDeviceListingProperties) => (
        <div>
          {currentRow.firstName || currentRow.lastName ? (
            <p style={{ fontWeight: 600 }}>
              {currentRow.firstName} {currentRow.lastName}
            </p>
          ) : null}
          <p style={{ fontSize: '11px', color: 'var(--grey-2)' }}>{currentRow.userCustomId}</p>
        </div>
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof PersonalDeviceListingProperties,
      sortAllow: true,
      queryParamsKey: 'personalChargerId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <PersonalDeviceSidePanel fetchIdFrom="queryParams" deviceModule />,
    },
    {
      label: t('chargerModel'),
      accessorKey: 'modelName' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      /**
       * Custom render: show brandName + modelName
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: PersonalDeviceListingProperties) => (
        <div>
          <p style={{ fontWeight: 600 }}>{currentRow.brandName}</p>
          <p style={{ fontSize: '11px', color: 'var(--grey-2)' }}>{currentRow.modelName}</p>
        </div>
      ),
    },
    {
      label: t('chargerSn'),
      accessorKey: 'chargingPointSn' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('installationType'),
      accessorKey: 'installationType' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      /**
       * Capitalize first letter
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: PersonalDeviceListingProperties) => (
        <p style={{ textTransform: 'capitalize' }}>{currentRow.installationType || '—'}</p>
      ),
    },
    {
      label: t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof PersonalDeviceListingProperties,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: PersonalDeviceListingProperties) => (
        <p>
          {ToFixedValue(currentRow.maximumOutputPower, 3)} {currentRow.maximumOutputPowerUnit}
        </p>
      ),
    },

    {
      label: t('chargerStatus'),
      accessorKey: 'isDeviceOffline' as keyof PersonalDeviceListingProperties,
      customData: renderStatus,
      sortAllow: false,
    },
    {
      label: t('Created'),
      accessorKey: 'createdAt' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
    },
    {
      label: t('Updated'),
      accessorKey: 'updatedAt' as keyof PersonalDeviceListingProperties,
      sortAllow: false,
    },
    // {
    //   label: 'Actions',
    //   accessorKey: '' as keyof PersonalDeviceListingProperties,
    //   columnWidth: 80,
    //   customData:
    //     /**
    //      * function for action buttons
    //      * @param currentRow
    //      * @returns
    //      */
    //     (currentRow: PersonalDeviceListingProperties) => (
    //       <div className="action-button-group">
    //         <div className="device-listing-actions-wrap">
    //           <span>
    //             <RefreshIcon onClick={handleSubmit} />
    //           </span>
    //           {(permissionCheck('delete_device') as any) && (
    //             <span
    //               role="presentation"
    //               onClick={(event) => handleDeleteDeviceWarning(currentRow, event)}>
    //               <DeleteIcon />
    //             </span>
    //           )}
    //         </div>
    //       </div>
    //     ),
    // },
  ];

  /**
   * Fetch the devices details in redus
   * @param {any} data - The data
   */
  const fetchData = (data: PersonalDeviceListingRequest) => {
    if (!loading) {
      const queryData = {
        ...data,
        deviceType: 'personal',
      };
      dispatch(fetchAllDevices(queryData));
    }
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setDeviceModalView({
      ...deviceModalView,
      openModal: false,
    });
  };
  /**
   * Table row click functionality
   * @param {any} id - The id
   */
  const handleClickTableRow = (id: string) => {
    // Set or update the logId param
    if (searchParameters.has('stationId')) {
      searchParameters.delete('stationId');
    }
    searchParameters.set('personalChargerId', id);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('endConsumerName'), accessorKey: 'userCustomId' },
    { label: t('chargerName'), accessorKey: 'chargingPointName' },
    { label: t('chargerModel'), accessorKey: 'modelName' },
    { label: t('chargerSn'), accessorKey: 'chargingPointSn' },
    { label: t('connectorType'), accessorKey: 'connectorType' },
    { label: t('installationType'), accessorKey: 'installationType' },
    { label: t('maxPowerOutput'), accessorKey: 'maximumOutputPower' },
    { label: t('chargerStatus'), accessorKey: 'isDeviceOffline' },
    { label: t('Created'), accessorKey: 'createdAt' },
    { label: t('Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter((col) => {
    return col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };
    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;
    personalDeviceApi
      .fetchPersonalChargerListExport(payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: PersonalDeviceListingProperties, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'userCustomId':
                return `${row.firstName} ${row.lastName} - ${row.userCustomId}` || t('na');

              case 'chargingPointName':
                return row?.chargingPointName || t('na');

              case 'modelName':
                return `${row.brandName} - ${row.modelName}` || t('na');

              case 'chargingPointSn':
                return row?.chargingPointSn || t('na');

              case 'connectorType':
                return row?.connectorType || t('na');

              case 'installationType':
                return row?.installationType || t('na');

              case 'maximumOutputPower':
                return `${ToFixedValue(row.maximumOutputPower, 3) || '0.000'} kW`;

              case 'isDeviceOffline':
                return row.isDeviceOffline ? t('offline') : t('online');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'personal-devices-list.csv');
        } else {
          exportPDF(headers, rows, 'personal-devices-list.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: PersonalDeviceListingRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...value, ...queryParameters, sortField, sortBy });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to auto refresh data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshDevices({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  useEffect(() => {
    if (isFilterUpdated || devices.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        sortField,
        sortBy,
      });

      dispatch(disableFiltersApplied());
    }
  }, [filters]);

  return (
    <div className="device-listing-wrap">
      {statusCounts?.totalDeviceCount === 12 && (
        <div className="stats-bar">
          {(() => {
            const elements: React.ReactNode[] = [];

            elements.push(
              <div className="stat-item" key="total">
                <span className="stat-label">Total</span>
                <span className="stat-value">{statusCounts?.totalDeviceCount ?? 0}</span>
              </div>,
            );

            const statusBadges: React.ReactNode[] = [];
            if ((statusCounts?.totalOnlineDeviceCount ?? 0) > 0) {
              statusBadges.push(
                <div className="stat-badge online-badge" key="online">
                  <span className="badge-dot" />
                  Online: {statusCounts?.totalOnlineDeviceCount}
                </div>,
              );
            }

            if ((statusCounts?.totalOfflineDeviceCount ?? 0) > 0) {
              statusBadges.push(
                <div className="stat-badge offline-badge" key="offline">
                  <span className="badge-dot" />
                  Offline: {statusCounts?.totalOfflineDeviceCount}
                </div>,
              );
            }

            if (statusBadges.length > 0) {
              elements.push(<React.Fragment key="status">{statusBadges}</React.Fragment>);
            }

            const visibleConnectorTypes = [
              'CCS1',
              'CCS2',
              'Type2',
              'CHAdeMO',
              'NACS',
              'J1772',
            ].filter((type) => {
              const count = statusCounts[type as keyof StatusCountsInterface] as number;
              return count > 0;
            });

            if (visibleConnectorTypes.length > 0) {
              elements.push(
                <div className="filter-chips-group" key="connectorType">
                  <span className="chips-label">Connector Type:</span>
                  <div className="chips-list">
                    {visibleConnectorTypes.map((types) => (
                      <span key={types} className="chip-item">
                        {types}: {statusCounts[types as keyof StatusCountsInterface]}
                      </span>
                    ))}
                  </div>
                </div>,
              );
            }

            return elements.map((element, index) => (
              <React.Fragment key={index}>
                {index > 0 && <div className="stat-divider" />}
                {element}
              </React.Fragment>
            ));
          })()}
        </div>
      )}
      <DeviceViewModel open={deviceModalView.openModal} handleCloseModal={handleCloseModal} />
      <div className="device-listing-container">
        <CustomTable
          dataKey="PersonalDeviceManagement"
          version="v2"
          header={column}
          data={devices}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          height={window.innerHeight >= 700 ? 327 : 288}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          rowClickEvent={handleClickTableRow}
          filters={filters}
          filtersToDispatch={updateChargingPointFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListChargingPointFilters}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          refreshRequired
          handleRefresh={handleRefresh}
          rowClickSidePanel={<PersonalDeviceSidePanel fetchIdFrom="queryParams" deviceModule />}
          rowClickParamsKey="personalChargerId"
          refershCurrentPages={handleRefresh}
          exportLoader={exportLoader}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          onHiddenColumnsChange={setHiddenColumns}
          hoverLabel={t('refresh')}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
        />
      </div>
    </div>
  );
}

export default PersonalDeviceTable;
